import axios from "axios";
import { API_BASE_URL } from "../../config/api";

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem("token");
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // Add authentication context for debugging
      config.metadata = {
        ...config.metadata,
        hasToken: true,
        timestamp: new Date().toISOString()
      };
    } else {
      console.warn('No authentication token found for request:', config.url);
      config.metadata = {
        ...config.metadata,
        hasToken: false,
        timestamp: new Date().toISOString()
      };
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 Unauthorized and enhance error handling
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful authenticated requests for debugging
    if (response.config.metadata?.hasToken) {
      console.log('Authenticated request successful:', {
        url: response.config.url,
        method: response.config.method,
        status: response.status
      });
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      console.error('401 Unauthorized error:', {
        url: error.config?.url,
        method: error.config?.method,
        hasToken: error.config?.metadata?.hasToken
      });
      
      // Clear all authentication-related items
      localStorage.removeItem("token");
      localStorage.removeItem("userId");
      localStorage.removeItem("userInfo");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userType");
      localStorage.removeItem("user");
      
      console.log('Authentication cleared due to 401 error');
      
      // Redirect to login page if not already there
      if (window.location.pathname !== '/') {
        console.log('Redirecting to login page due to 401 error');
        window.location.href = '/';
      }
    } else if (error.response?.status === 403) {
      console.error('403 Forbidden error - insufficient permissions:', {
        url: error.config?.url,
        method: error.config?.method
      });
    } else if (error.response?.status >= 500) {
      console.error('Server error:', {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
    }
    
    return Promise.reject(error);
  }
);

// Utility function to validate authentication before making requests
export const validateAuthBeforeRequest = () => {
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("userRole");
  
  if (!token) {
    console.error('No authentication token available');
    return false;
  }
  
  if (!userRole) {
    console.error('No user role available');
    return false;
  }
  
  return true;
};

// Enhanced axios instance with authentication validation
export const authenticatedRequest = async (config) => {
  if (!validateAuthBeforeRequest()) {
    throw new Error('Authentication required');
  }
  
  return axiosInstance(config);
};

export default axiosInstance;
